import { useTranslation } from "react-i18next";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { DialogResult, DialogState, BindDialogReturn, useDialogState, bindDialog, DialogProps } from "../../../hooks/use-dialog-state/UseDialogState";
import { hiddenRecipients } from "../../../services/reporting-data-service/ReportScheduleModels";
import FormInput from "../../forms/form-input/FormInput";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import ResponsiveDialog from "../../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../../unsaved-changes-prompt/UnsavedChangesPrompt";
import Form from "../../forms/form/Form";

export type AddEmailDialogInitValues = {
};

export type AddEmailDialogReturnValues = {
    emailAddress: string;
};

export type AddEmailDialogResult = DialogResult<AddEmailDialogReturnValues>;
export type AddEmailDialogProps = DialogProps<AddEmailDialogInitValues, AddEmailDialogResult>;

export type AddEmailDialogFormValuesType = {
    emailAddress: string;
};

const defaultFormValues: AddEmailDialogFormValuesType = {
    emailAddress: ''
};

export default function AddEmailDialog({
    open,
    onClose,
    values,
}: AddEmailDialogProps): JSX.Element {

    const [userState] = useUserContext();
    const { t } = useTranslation();
    const { confirm } = useConfirmation();

    const form = useForm<AddEmailDialogFormValuesType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        //setValue,
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        //watch,
        //trigger
    } = form;

    useEffect(() => {
        if (open) {

            if (values) {
                reset({
                    emailAddress: ''
                });
            }
        } else {
            reset(defaultFormValues);
        }
    }, [reset, values, open]);

    async function onOkay(data: AddEmailDialogFormValuesType) {

        onClose && onClose({
            reason: "ok",
            values: {
                emailAddress: data.emailAddress
            }
        });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getCancelConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    const _validateName = (value: string): boolean => {
        return !(!userState.user?.isElynxAdmin && hiddenRecipients.find(o => o === value.trim().toLowerCase()) !== undefined) ;
    }

    return (
        <>
            <ResponsiveDialog
                title={t("actions.addRecipient")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    async (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>

                    <FormPropertySection label="">

                        <FormInput
                            name="emailAddress"
                            label={t("recipientEditDialog.emailAddress")}
                            sx={{
                                width: 1
                            }}
                            rules={{
                                pattern: {
                                    value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                    message: t("emailRequired")
                                },
                                required: t("emailRequired"),
                                validate: (value: string) => !_validateName(value) ?
                                    t('scheduledReports.reservedEmail') : 
                                true
                            }}
                        />

                    </FormPropertySection>

                </Form>
            </ResponsiveDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type AddEmailDialogState = DialogState<AddEmailDialogInitValues, AddEmailDialogResult>;
export type BindAddEmailDialogReturn = BindDialogReturn<AddEmailDialogInitValues, AddEmailDialogResult>;

export function useAddEmailDialogState(): AddEmailDialogState {
    return useDialogState();
}

export function bindAddEmailDialog(props: AddEmailDialogState): BindAddEmailDialogReturn {
    return bindDialog(props);
}
